import { z } from "zod";
import { FACTION_IDS } from "@frontier/content/factions";

export const CHAT_CHANNEL_IDS = [
  "global",
  "faction",
  "location",
  "party",
] as const;
export const CHAT_TEXT_LIMIT = 280;
export const CHAT_BUFFER_LIMIT = 100;
export const chatChannelSchema = z.enum(CHAT_CHANNEL_IDS);
export type ChatChannelId = z.infer<typeof chatChannelSchema>;
const contextIdSchema = z.uuid();
const textSchema = z
  .string()
  .trim()
  .min(1)
  .max(CHAT_TEXT_LIMIT)
  .refine(
    (text) =>
      !/[\u0000-\u001f\u007f-\u009f\u202a-\u202e\u2066-\u2069]/u.test(text),
    "Control characters are not allowed",
  );
export const chatSendSchema = z
  .object({
    requestId: z.uuid(),
    contextId: contextIdSchema,
    channel: chatChannelSchema,
    text: textSchema,
  })
  .strict();
export type ChatSend = z.infer<typeof chatSendSchema>;
export const chatSyncSchema = z.object({}).strict();
export const chatScopeSchema = z
  .object({
    channel: chatChannelSchema,
    scopeId: z.string().min(1).max(128),
    label: z.string().min(1).max(80),
  })
  .strict();
export type ChatScope = z.infer<typeof chatScopeSchema>;
export const chatMessageSchema = z
  .object({
    id: z.uuid(),
    channel: chatChannelSchema,
    scopeId: z.string().min(1).max(128),
    senderId: z.uuid(),
    senderName: z.string().min(1).max(24),
    factionId: z.enum(FACTION_IDS).nullable(),
    text: textSchema,
    sentAt: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
  })
  .strict();
export type ChatMessage = z.infer<typeof chatMessageSchema>;
export const chatContextSchema = z
  .object({
    type: z.literal("context"),
    contextId: contextIdSchema,
    scopes: z
      .array(chatScopeSchema)
      .max(CHAT_CHANNEL_IDS.length)
      .refine(
        (scopes) =>
          new Set(scopes.map((scope) => scope.channel)).size === scopes.length,
      ),
  })
  .strict();
export type ChatContext = z.infer<typeof chatContextSchema>;
export const chatPacketSchema = z.discriminatedUnion("type", [
  chatContextSchema,
  z
    .object({
      type: z.literal("message"),
      contextId: contextIdSchema,
      message: chatMessageSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal("accepted"),
      contextId: contextIdSchema,
      requestId: z.uuid(),
      message: chatMessageSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal("rejected"),
      contextId: contextIdSchema,
      requestId: z.uuid(),
      code: z.string().min(1).max(64),
      message: z.string().min(1).max(200),
    })
    .strict(),
]);
export type ChatPacket = z.infer<typeof chatPacketSchema>;
